import { useDialogState, useLoadingState } from 'hooks'
import { VehicleNeedsClusterRemovedPhotoProps } from './index'
import { useState } from 'react'
import PulledClusterPhotoDialog from './PulledClusterPhotoDialog'
import * as controllers from 'controllers'
import { enqueueSnackbar } from 'notistack'
import { Button, Card, CardContent, Divider, Grid } from '@mui/material'
import NeedsClusterRemovedStepper from './NeedsClusterRemovedStepper'
import { NeedsClusterRemovedDetailsDto, NeedsClusterRemovedPulledClusterPhotoDto, VehicleDto } from 'dtos'
import ClusterRemovedPhotoCard from './ClusterRemovedPhotoCard'
import { ArrowRight, ArrowLeft } from '@mui/icons-material'
import { ExplanationAccordion, GreenText } from 'components'

interface Step2PulledClusterPhotoProps extends VehicleNeedsClusterRemovedPhotoProps {
  onBack: () => void
  onNext: () => void
  needsClusterRemovedDetails: NeedsClusterRemovedDetailsDto
  vehicleGuid?: string
}

export default function Step2PulledClusterPhoto({
  onBack,
  onNext,
  needsClusterRemovedDetails,
  vehicleGuid,
  pulledClusterPhoto,
  setPulledClusterPhoto
}: Step2PulledClusterPhotoProps) {
  const [loadingState, setLoadingState] = useLoadingState({
    isUpdatingPulledClusterPhoto: false,
    isDeletingPulledClusterPhoto: false
  })

  const [dialogState, setDialogState] = useDialogState({
    pulledClusterPhotoDialogOpen: false
  })

  const hasPulledClusterPhoto = Boolean(pulledClusterPhoto.documentGuid)

  return (
    <>
      <PulledClusterPhotoDialog
        initialValues={pulledClusterPhoto}
        isLoading={loadingState.isDeletingPulledClusterPhoto || loadingState.isUpdatingPulledClusterPhoto}
        onClose={() => {
          setDialogState('pulledClusterPhotoDialogOpen', false)
        }}
        onDelete={() => {
          setLoadingState('isDeletingPulledClusterPhoto', true)
          controllers
            .deleteNeedsClusterRemovedPulledClusterPhoto(vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Pulled Cluster Photo Deleted Successfully', { variant: 'success' })
              setPulledClusterPhoto(response.value)
              setDialogState('pulledClusterPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingPulledClusterPhoto', false)
            })
        }}
        onSave={values => {
          setLoadingState('isUpdatingPulledClusterPhoto', true)
          controllers
            .updateNeedsClusterRemovedPulledClusterPhotoInformation(vehicleGuid!, values)
            .then(response => {
              console.log('API Response:', response) // Check response structure
              if (response?.value) {
                setPulledClusterPhoto(response.value)
                enqueueSnackbar('Pulled Cluster Photo Updated Successfully', { variant: 'success' })
              } else {
                console.error('Unexpected API Response:', response)
              }
              setDialogState('pulledClusterPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isUpdatingPulledClusterPhoto', false)
            })
        }}
        open={dialogState.pulledClusterPhotoDialogOpen}
        vehicleGuid={vehicleGuid!}
      />

      <Card>
        <CardContent>
          <Grid container spacing={1} alignItems='center'>
            <Grid item xs={12}>
              <NeedsClusterRemovedStepper activeStep={1} />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12}>
              <ClusterRemovedPhotoCard
                complete={Boolean(pulledClusterPhoto.documentGuid)}
                documentGuid={pulledClusterPhoto.documentGuid}
                onClick={() => {
                  setDialogState('pulledClusterPhotoDialogOpen', true)
                }}
                type='pulled-cluster'
                vehicleGuid={vehicleGuid}
              />
            </Grid>

            <Grid item xs={12} marginTop={2}>
              <Button
                color='primary'
                endIcon={<ArrowRight />}
                fullWidth
                onClick={onNext}
                type='submit'
                variant='contained'
                disabled={!hasPulledClusterPhoto}
              >
                NEXT
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Button color='primary' startIcon={<ArrowLeft />} fullWidth onClick={onBack} variant='outlined'>
                PREVIOUS
              </Button>
            </Grid>

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**Vehicle Check-In Steps:**

Displays the current step of the vehicle in the Needs Cluster 
Removed process.

---

**Pulled Cluster:**

Displays the Pulled Cluster image uploaded by the user.

If no image has been uploaded, a “No Image” icon will be 
displayed instead.

Clicking the Pulled Cluster image will open the Capture Pulled 
Cluster Photo Dialog.

---

**NEXT Button:**

Clicking this button will:

- Save the uploaded photo to the database.

- Advance the user to the next step in the Needs Cluster 
Removed process.

This button remains disabled until a photo has been uploaded.

---

**PREVIOUS Button:**

Clicking this button will:

- Discard any uploaded photo that has not been saved.

- Return the user to the previous step in the process.

---

          `}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
